import React, { useEffect, useMemo, useState, useRef } from "react";
import "./UserGame.css";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getManagerSettings } from "../../functions/getManagerSettings";
import { QRCodeCanvas } from "qrcode.react";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";

export default function UserGame() {
  const location = useLocation();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [lobbyPin, setLobbyPin] = useState("");
  const [lobbyLink, setLobbyLink] = useState("");
  const [loading, setLoading] = useState(false);
  const qrRef = useRef();

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);

  const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
  const themeName = "guess_me";

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, storedUserData]);

  const lobbyName = useMemo(() => {
    const st = location.state || {};
    if (st?.lobby_name) return String(st.lobby_name);
    try {
      const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
      if (cached?.lobby_name) return String(cached.lobby_name);
    } catch {
      /* ignore */
    }
    return "";
  }, [location.state]);

  useEffect(() => {
    document.documentElement.classList.add("usergame-static-bg");
    document.body.classList.add("usergame-static-bg");
    return () => {
      document.documentElement.classList.remove("usergame-static-bg");
      document.body.classList.remove("usergame-static-bg");
    };
  }, []);

  useEffect(() => {
    if (!tokenForApi || !uid) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await getManagerSettings({ token: tokenForApi, userId: uid, themeName });
        if (cancelled) return;
        setLobbyPin(resp?.lobby_pin != null ? String(resp.lobby_pin) : "");
        setLobbyLink(resp?.lobby_link != null ? String(resp.lobby_link) : "");
      } catch (e) {
        if (!cancelled) {
          await Swal.fire("Error", e?.message || "Failed to load lobby", "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, uid, themeName]);

  const copyLink = async () => {
    if (!lobbyLink) return;
    try {
      await navigator.clipboard.writeText(lobbyLink);
      await Swal.fire("Copied", "Lobby link copied to clipboard", "success");
    } catch {
      await Swal.fire("Copy failed", "Please copy manually", "error");
    }
  };

  const qrDownloadName = useMemo(() => {
    const pin = String(lobbyPin || "").trim();
    const fromLobby = String(lobbyName || "").match(/LOBBY-(\d{5})/i)?.[1] || "";
    const code = pin || fromLobby || "code";
    return `who-wants-to-be-a-champion-${code}.png`;
  }, [lobbyPin, lobbyName]);

  const downloadQR = () => {
    if (!qrRef.current) return;
    try {
      const canvas = qrRef.current.querySelector("canvas");
      if (!canvas) return;
      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = qrDownloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      Swal.fire("Downloaded", "QR code downloaded successfully", "success");
    } catch {
      Swal.fire("Error", "Failed to download QR code", "error");
    }
  };

  const handleStart = () => {
    if (!lobbyName) return;
    navigate("/quiz-ui", { replace: true, state: { lobby_name: lobbyName, backTo: "/usergame" } });
  };

  return (
    <div className="ug-page">
      <div className="ug-inner">

        <div className="ug-grid">
          <div className="ug-card">
            <div className="ug-card-title">JOIN OPTIONS</div>

            <div className="ug-row">
              {/* QR Box */}
              <div className="ug-qr-box" ref={qrRef}>
                <div className="ug-qr-label">Scan to Join</div>
                <div className="ug-qr-canvas-wrap">
                  {lobbyLink
                    ? <QRCodeCanvas value={lobbyLink} size={160} className="ug-qr-canvas" />
                    : <div className="ug-qr-placeholder" />}
                </div>
                <button
                  className="ug-btn ug-btn-copy"
                  onClick={copyLink}
                  disabled={!lobbyLink || loading}
                >
                  Copy Link
                </button>
                <button
                  className="ug-btn ug-btn-download"
                  onClick={downloadQR}
                  disabled={!lobbyLink || loading}
                >
                  📥 Download QR
                </button>
              </div>

              {/* PIN Box */}
              <div className="ug-pin-box">
                <div className="ug-pin-label">JOIN BY PIN</div>
                <div className="ug-pin">
                  {lobbyPin || (loading ? "..." : "—")}
                </div>
              </div>
            </div>

            <button
              className="ug-btn ug-btn-start"
              onClick={handleStart}
              disabled={!lobbyLink || loading}
            >
              START GAME
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
